import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import BrandWordmark from "./BrandWordmark";
import { useTranslation } from "./i18n";
import LanguageSwitcher from "./LanguageSwitcher";
import { continuationUrl } from "./lib/continuationUrl";
import { useWorkspaceScreen } from "./lib/useWorkspaceScreen";

export default function DesktopViewportGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation("screenSupport");
  const { pathname } = useLocation();
  const policyPage = ["/privacy", "/terms", "/data-processing"].includes(pathname);
  const desktop = useWorkspaceScreen();
  const [opened, setOpened] = useState(desktop);
  const heading = useRef<HTMLHeadingElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const [copyResult, setCopyResult] = useState<{ url: string; state: "copying" | "copied" | "manual" }>();
  const linkField = useRef<HTMLInputElement>(null);
  const supported = desktop || policyPage;
  const entryUrl = continuationUrl(window.location.href);
  const copyState = copyResult?.url === entryUrl ? copyResult.state : "idle";

  useEffect(() => {
    if (copyState === "manual") {
      linkField.current?.focus();
      linkField.current?.select();
    }
  }, [copyState]);

  async function copyEntry() {
    setCopyResult({ url: entryUrl, state: "copying" });
    try {
      await navigator.clipboard.writeText(entryUrl);
      setCopyResult({ url: entryUrl, state: "copied" });
    } catch {
      setCopyResult({ url: entryUrl, state: "manual" });
    }
  }

  useEffect(() => {
    if (supported) {
      if (desktop) setOpened(true);
      if (previousFocus.current?.isConnected) previousFocus.current.focus();
      previousFocus.current = null;
    } else {
      previousFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      heading.current?.focus();
    }
  }, [desktop, supported]);

  return (
    <>
      {!supported && (
        <main className="screen-support-shell" id="main-content" aria-labelledby="screen-support-title">
          <LanguageSwitcher />
          <section className="screen-support-card">
            <Link className="brand-home" to="/">
              <BrandWordmark />
            </Link>
            <p className="eyebrow">{t("eyebrow")}</p>
            <h1 id="screen-support-title" ref={heading} tabIndex={-1}>
              {t("title")}
            </h1>
            <p className="screen-support-description">{t("description")}</p>
            <section className="screen-support-continuation" aria-labelledby="continuation-title">
              <h2 className="screen-support-continuation-title" id="continuation-title">
                {t("continuationTitle")}
              </h2>
              <p className="screen-support-continuation-text">{t("continuationDescription")}</p>
              <button
                type="button"
                className="screen-support-copy"
                disabled={copyState === "copying"}
                onClick={() => void copyEntry()}
              >
                {t(copyState === "copying" ? "copying" : "copyLink")}
              </button>
              <p
                className="screen-support-continuation-text screen-support-copy-status"
                role="status"
                aria-atomic="true"
              >
                {copyState === "copied" ? t("copied") : copyState === "manual" ? t("copyManually") : ""}
              </p>
              {copyState === "manual" && (
                <label className="screen-support-manual-link">
                  {t("entryLink")}
                  <input
                    className="screen-support-url"
                    ref={linkField}
                    value={entryUrl}
                    readOnly
                    onFocus={(event) => event.currentTarget.select()}
                  />
                </label>
              )}
              <p className="screen-support-continuation-text screen-support-data-note">{t("continuationDataNote")}</p>
            </section>
            <p className="screen-support-detail">{t("detail")}</p>
            <nav className="screen-support-legal" aria-label={t("legalLabel")}>
              <Link className="screen-support-policy-link" to="/privacy">
                {t("privacy")}
              </Link>
              <Link className="screen-support-policy-link" to="/terms">
                {t("terms")}
              </Link>
              <Link className="screen-support-policy-link" to="/data-processing">
                {t("dataProcessing")}
              </Link>
            </nav>
          </section>
        </main>
      )}
      {/* Keep the opened console mounted so resizing cannot discard drafts or interrupt storage. */}
      {(opened || policyPage) && (
        <div hidden={!supported} inert={!supported}>
          {children}
        </div>
      )}
    </>
  );
}
